import { CreditCard, FileText } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { formatCurrency, formatDateTimeValue } from '@/lib/utils';

import { TablaDelTurno } from '../components/tabla-turno';
import type { ColumnaDelTurno } from '../components/tabla-turno';
import { turnoApi, useCreditosDelTurno, useTurnoActual } from '../turno.api';
import type { CreditoDelTurno } from '../turno.types';

/**
 * Vales a crédito del turno (`/turno/creditos`).
 *
 * Puerto de `Turno::credits`: las ventas a crédito (`credito_venta`) que el grifero
 * registró en su turno, con el vale en PDF (el panel anterior sólo podía imprimirlo
 * con QZ Tray).
 */
export function TurnoCreditosPage() {
  useDocumentTitle('Turno · Vales créditos');

  const turno = useTurnoActual();

  const [pagina, setPagina] = useState(1);
  const [buscar, setBuscar] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const listado = useCreditosDelTurno({ page: pagina, pageSize: 25, buscar });
  const filas = listado.data?.items ?? [];

  const verPdf = async (fila: CreditoDelTurno) => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(turnoApi.ticketCredito(fila.id), { nombre: `vale-${fila.id}` });
    } catch (error) {
      setAviso(error instanceof Error ? error.message : 'No se pudo abrir el vale.');
    }
  };

  const columnas: ColumnaDelTurno<CreditoDelTurno>[] = [
    { encabezado: 'Fecha', celda: (fila) => formatDateTimeValue(fila.created_at) },
    {
      encabezado: 'Cliente',
      celda: (fila) => (
        <span className="block">
          <span className="block text-ink-800">{fila.cliente_nombre ?? '—'}</span>
          <span className="text-xs text-ink-400">{fila.cliente_numero ?? ''}</span>
        </span>
      ),
    },
    { encabezado: 'Placa', celda: (fila) => fila.cliente_placa ?? '—' },
    {
      encabezado: 'Vale',
      celda: (fila) => <Badge variant="neutral">{fila.comprobante_serie ?? `#${fila.id}`}</Badge>,
    },
    {
      encabezado: 'Descuento',
      clase: 'text-right',
      celda: (fila) => formatCurrency(fila.descuento ?? 0),
    },
    {
      encabezado: 'Monto',
      clase: 'text-right',
      celda: (fila) => (
        <span className="font-medium text-ink-800">{formatCurrency(fila.monto_pagado ?? 0)}</span>
      ),
    },
    {
      encabezado: 'Vence',
      celda: (fila) => (fila.fecha_vencimiento ? formatDateTimeValue(fila.fecha_vencimiento) : '—'),
    },
    {
      encabezado: 'Acciones',
      celda: (fila) => (
        <Button
          variant="outline"
          size="sm"
          leftIcon={<FileText className="size-4" aria-hidden="true" />}
          onClick={() => void verPdf(fila)}
        >
          Vale PDF
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Operador</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <CreditCard className="size-6 text-brand-500" aria-hidden="true" />
          Vales créditos del turno
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-ink-500">
          Ventas a crédito registradas en tu turno. El vale se entrega al cliente y queda en su
          cuenta corriente.
        </p>
      </header>

      {aviso ? (
        <Alert variant="error" title="No se pudo abrir el vale">
          {aviso}
        </Alert>
      ) : null}

      <TablaDelTurno
        titulo="Vales del turno"
        descripcion={
          turno.data?.abierto
            ? `Turno #${turno.data.turno?.id ?? ''} abierto.`
            : 'Cuando abras tu turno verás aquí sus vales.'
        }
        columnas={columnas}
        filas={filas}
        total={listado.data?.total ?? 0}
        pagina={pagina}
        tamanioPagina={25}
        buscar={buscar}
        onBuscar={(valor) => {
          setBuscar(valor);
          setPagina(1);
        }}
        onPagina={setPagina}
        cargando={listado.isLoading}
        vacio="Todavía no hay vales a crédito en este turno."
        sinTurno={turno.data ? !turno.data.abierto : false}
        avisoSinTurno="sus vales"
      />
    </div>
  );
}
